import {
	faFaceSmile as farFaceSmile,
	faFileLines as farFileLines,
	faThumbsUp as farThumbsUp,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faCircleCheck as fasCircleCheck } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { Config } from "@/app/config";
import { GoogleAnalytics4 } from "@/shared/lib/google-analytics-4";
import { __, ButtonLegacy } from "@/shared/ui";
import type { AbstractStepProps } from "./AbstractStep";
import { AbstractStep } from "./AbstractStep";

export interface BasicSuccessPageForDataFormProps extends AbstractStepProps {
	title?: string;
	userEmail: string;
	leadId: number;
	relativeUrl: string;
	leadUuid?: string;
	ga4SendEventParameters: {
		providerName: string;
		productName: string;
		productId: string;
	};
}

interface DataFromListItem {
	icon: ReactNode;
	content: ReactNode;
}

export class BasicSuccessPageForDataForm<
	FormFields extends Record<string, any> = {},
	Props extends BasicSuccessPageForDataFormProps = BasicSuccessPageForDataFormProps,
> extends AbstractStep<FormFields, Props> {
	public static defaultProps = {
		...AbstractStep.defaultProps,
		title: __("Köszönjük, hogy leadtad érdeklődésed!"),
	};

	protected renderContent(): ReactNode {
		const { title } = this.props;

		return (
			<>
				<Card.Header>
					<Card.Title className="text-center h6">{title}</Card.Title>
				</Card.Header>
				{this.renderSuccessBody()}
				{this.renderDataFormBody()}
				{this.renderDataFormFooter()}
			</>
		);
	}

	protected renderSuccessBody(): ReactNode {
		return (
			<Card.Body className="d-flex flex-column disappear">
				<div className="my-auto py-2 text-center">
					<FontAwesomeIcon icon={fasCircleCheck} className="mb-2 text-gold" size="4x" />
					<br />
					Az eredményeket elküldtük az email címedre.
				</div>
				<div className="text-center font-weight-bold pb-3">
					Ha nem érkezik meg perceken belül, ellenőrizd leveleződ „spam/levélszemét” mappáját is!
				</div>
			</Card.Body>
		);
	}

	protected renderDataFormBody(
		title = "Gyorsan van szükséged lakáshitelre?",
		subTitle = "24-48 órán belül:",
		items: DataFromListItem[] = [
			{
				icon: <FontAwesomeIcon icon={farFaceSmile} />,
				content: (
					<>
						előzetes <strong>hitelminősítés</strong>
					</>
				),
			},
			{
				icon: <FontAwesomeIcon icon={farThumbsUp} />,
				content: (
					<>
						személyes <strong>hitelajánlat</strong>
					</>
				),
			},
		],
	): ReactNode {
		return (
			<Card.Body className="bg-bm-blue d-flex flex-column pb-1">
				<div className="text-center text-uppercase display-5 font-weight-bold mb-0 py-3">{title}</div>
				<div className="text-center display-5 mb-0">{subTitle}</div>
				<div className="align-self-center mx-2 mt-2 mb-3">
					{items.map(({ icon, content }, index) => this.renderDataFromListItem(icon, content, index))}
				</div>
			</Card.Body>
		);
	}

	protected renderDataFromListItem(icon: ReactNode, content: ReactNode, index: number): ReactNode {
		return (
			<div className="mt-3 d-flex align-items-center" key={`dfl-item-${index}`}>
				<span className="fa-3x text-gold mr-3">{icon}</span>
				<span className="h4 font-weight-normal">{content}</span>
			</div>
		);
	}

	protected renderDataFormFooter(buttonTitle = "Gyorsított lakáshitel"): ReactNode {
		return (
			<Card.Footer className="py-4 bg-bm-blue text-center">
				<ButtonLegacy
					variant="gold"
					href={this.createUrl()}
					className="text-uppercase"
					icon={<FontAwesomeIcon icon={farFileLines} />}
					target="_blank"
					onClick={this.onDataFormButtonClick}
				>
					{buttonTitle}
				</ButtonLegacy>
			</Card.Footer>
		);
	}

	protected createUrl(): string {
		const { leadId, userEmail, relativeUrl, leadUuid } = this.props;

		return Config.getBankmonitorUrl(relativeUrl, {
			li: leadId,
			email: userEmail,
			lead_uuid: leadUuid,
		});
	}

	@autobind
	private onDataFormButtonClick(): void {
		const { userEmail, ga4SendEventParameters, leadUuid } = this.props;

		this.sendEvent("BM_fastTrack", {
			buttonId: GoogleAnalytics4.generateId("bi"),
			leadUuid: leadUuid,
			email: userEmail,
			user_data: {
				email_address: userEmail,
			},
			...ga4SendEventParameters,
		});
	}

	protected getClassName(): string {
		return compact([super.getClassName(), "data bg-success success-step data-form-step"]).join(" ");
	}

	protected getComponentName(): string {
		return "BasicSuccessPageForDataForm";
	}
}
